import { Styles } from './styles';
import Box from '@mui/material/Box';
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Close, Remove } from '@mui/icons-material';
import { useStyles } from '../../../theme/styles.helpers';
import { useTranslation } from 'react-i18next';
import React, { useContext, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { AuthContext } from '../../../aws/AuthProvider';
import { generateRezept, listShopifyProducts, updateRequest } from '../../../aws/RequestsApi';

interface Props {
  onModalClose: () => void;
  selectedRequest: {
    requestId: string;
    email: string;
  };
  onRefetch?: () => void;
}

const PresignCreateModal = ({ onModalClose, onRefetch, selectedRequest }: Props) => {
  const { email } = selectedRequest;
  // Component for creating prescriptions
  const { t } = useTranslation();
  const styles = useStyles(Styles, {});
  const { apiClient } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [dob, setDob] = useState<string>('');
  // The presign request is created from an e-mail alone, so the patient name has
  // to be entered here - without it the Rezept PDF prints an empty name.
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [qAnda, setQAndA] = useState<{ question: string; answer: string }[]>([
    {
      question: 'Gibt es Haarausfall in Deiner Familie?',
      answer: '',
    },
    {
      question: 'Weist Deine Kopfhaut eines oder mehrere der folgenden Symptome auf?',
      answer: '',
    },
  ]);

  const [newQuestion, setNewQuestion] = useState<string>('');
  const [products, setProducts] = useState<
    { id: string; title: string; metafields: { key: string; value: string }[] }[]
  >([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [productsLoading, setProductsLoading] = useState(false);

  // Fetch Shopify products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      if (apiClient) {
        setProductsLoading(true);
        try {
          const productsData = await listShopifyProducts(apiClient, 'cache-first');
          setProducts(productsData);
        } catch (error) {
          console.error('Error fetching products:', error);
        } finally {
          setProductsLoading(false);
        }
      }
    };

    fetchProducts();
  }, [apiClient]);

  const handleProductsSelectChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    setSelectedProductId(value);
  };

  const updatePresignRequest = async (requestId: string) => {
    if (!apiClient) return;

    try {
      const selectedProduct = products.find((p) => p.id === selectedProductId);
      if (!selectedProduct) {
        console.error('Selected product not found');
        return;
      }
      const recipeInfoMetafield =
        (selectedProduct.metafields.find((mf) => mf.key === 'rezeptangaben') || {}).value || '[]';

      const recipe = JSON.parse(recipeInfoMetafield);
      const recipeInformation = Array.isArray(recipe) ? recipe[0] : recipe;

      const updatedData = {
        orderState: 'ALLOWED',
        requestId,
        product: {
          productId: selectedProduct.id.split('/').pop() || '',
          productName: selectedProduct.title,
          recipeInformation: {
            ...recipeInformation,
          },
        },
      };

      await updateRequest(selectedRequest.requestId, 'PRESIGN', updatedData, apiClient);
      console.log('Prescription request updated successfully');
    } catch (error) {
      console.error('Error updating prescription request:', error);
    }
  };

  const createForm = async () => {
    try {
      const res = await fetch('https://8kfpncel04.execute-api.eu-central-1.amazonaws.com/v1/shopify/survey', {
        // Prod
        // await fetch('https://lt32u2zq98.execute-api.eu-central-1.amazonaws.com/v1/shopify/survey', { // Dev

        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assets: {
            survey: {
              survey: 1,
              name: 'Umfrage 1',
              clientID: 'XY',
              projekt: 'YZ',
              uUID: 'uUid_test',
              createdAt: '',
              updatedAt: '',
              config: '',
              steps: [],
            },
            answers: qAnda.map(({ question, answer }) => ({
              question,
              answer,
              type: 'SingleSelectAnswer',
            })),
            images: [],
          },
          survey_succeeded: true,
          email: email.trim(),
          customerDob: dob.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        }),
      });

      return await res.json();
    } catch (error) {
      console.error('Error creating form:', error);
      return null;
    }
  };

  const createPrescription = async () => {
    setLoading(true);
    if (!apiClient) return;
    const selectedProduct = products.find((p) => p.id === selectedProductId);
    if (!selectedProduct) {
      console.error('Selected product not found');
      return;
    }
    console.log('selectedRequest.requestId: ', selectedRequest.requestId);
    const productId = selectedProduct.id.split('/').pop() || '';

    const formData = await createForm();
    if (!formData) return;
    console.log('formData: ', formData);

    // Add selected product to the request
    await updatePresignRequest(formData.requestId);

    // Add sproof data to the request
    const response = await generateRezept(productId, selectedRequest.requestId, '', false, false, apiClient);
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const doc = response.members.find((m: any) => m.isSigner);
    window.open(`https://sign.sproof.io/#/editor/${doc?.id}`, '_blank')?.focus();

    // Refetch presign requests list in parent component
    if (onRefetch) {
      onRefetch();
    }
    onModalClose();
    setLoading(false);
  };

  const isFormValid = () => {
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    return (
      email.trim() !== '' &&
      dob.trim() !== '' &&
      // Same rule as the survey's own name fields (see survey-form lib/contact).
      firstName.trim().length >= 2 &&
      lastName.trim().length >= 2 &&
      qAnda.every((item) => item.answer.trim() !== '') &&
      isEmailValid &&
      qAnda.length > 0 &&
      selectedProductId !== ''
    );
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Typography variant="h4">{t('Create new recipe for') + `: ${email}`}</Typography>
        </Box>

        <Box sx={styles.details}>
          <Box sx={styles.leftContainer}>
            {qAnda.map((item, index) => (
              <Box key={index} mb={4} display="flex" flexDirection="column" gap={2}>
                <Typography>
                  Q {index + 1}: {item.question}
                </Typography>

                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    label={t('Answer') + ` ${index + 1}`}
                    variant="outlined"
                    value={item.answer}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newQAndA = [...qAnda];
                      newQAndA[index].answer = event.target.value;
                      setQAndA(newQAndA);
                    }}
                    fullWidth
                  />
                  <Button
                    sx={styles.button}
                    onClick={() => {
                      const newQAndA = qAnda.filter((_, i) => i !== index);
                      setQAndA(newQAndA);
                    }}
                  >
                    <SvgIcon sx={{ color: 'primary.main' }} component={Remove}></SvgIcon>
                  </Button>
                </Box>
              </Box>
            ))}

            <Box display="flex" justifyContent="flex-end" gap={2} alignItems={'center'}>
              <FormControl sx={{ width: '100%' }} variant="outlined">
                <TextField
                  label={t('Add new question')}
                  variant="outlined"
                  value={newQuestion}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewQuestion(event.target.value)}
                />
              </FormControl>
              <Button
                sx={styles.button}
                disabled={!newQuestion.trim()}
                onClick={() => {
                  setQAndA([...qAnda, { question: newQuestion.trim(), answer: '' }]);
                  setNewQuestion('');
                }}
              >
                <AddIcon />
              </Button>
            </Box>

            {/* Product Selection Section */}
            <Box sx={{ mt: 4 }}>
              {productsLoading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress />
                </Box>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="prescription-products-label">{t('Product')}</InputLabel>
                  <Select
                    labelId="prescription-products-label"
                    value={selectedProductId}
                    onChange={handleProductsSelectChange}
                    input={<OutlinedInput label={t('Product')} />}
                    renderValue={(selected) => products.find((d) => d.id === selected)?.title || ''}
                  >
                    {products.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        <Box display="flex" justifyContent="space-between" width="100%">
                          <Typography noWrap>{product.title}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>
          <Box sx={styles.rightContainer}>
            <Box display="flex" flexDirection={'column'} gap={4}>
              <Typography>Email: {email}</Typography>

              <TextField
                variant="outlined"
                label={t('Firstname')}
                value={firstName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value)}
                fullWidth
              />

              <TextField
                variant="outlined"
                label={t('Lastname')}
                value={lastName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLastName(event.target.value)}
                fullWidth
              />

              <TextField
                variant="outlined"
                type={'date'}
                value={dob}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDob(event.target.value)}
                inputProps={{ lang: 'de' }}
                fullWidth
              />
            </Box>

            <Button sx={styles.button} onClick={createPrescription} disabled={!isFormValid()}>
              {loading ? <CircularProgress size={24} thickness={5} /> : <Typography>{t('Create recipe')}</Typography>}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Closing Icon */}
      <Box onClick={onModalClose} sx={{ marginLeft: 2, cursor: 'pointer' }}>
        <SvgIcon sx={{ color: 'primary.main' }} component={Close}></SvgIcon>
      </Box>
    </Box>
  );
};

export default PresignCreateModal;
